import config from '../config';
import {
    clearAccessToken, getAccessToken, isTokenUsable, setAccessToken
} from './accessToken';

const REFRESH_ENDPOINT = `${config.serverURL}/auth/refresh`;
const LOGOUT_ENDPOINT = `${config.serverURL}/auth/logout`;

/** The refresh produced a fresh access token. */
export const REFRESH_OK = 'ok';
/** The server said the refresh token is unknown, expired or revoked: log in again. */
export const REFRESH_SESSION_ENDED = 'session-ended';
/** The request never got an answer (offline, DNS, CORS). Says nothing about the session. */
export const REFRESH_NETWORK_ERROR = 'network-error';
/** The server answered, but not with a token (5xx, unparseable body). */
export const REFRESH_SERVER_ERROR = 'server-error';

let inFlightRefresh = null;

/**
 * Exchanges the httpOnly refresh cookie for a new access token.
 *
 * Uses `fetch` directly rather than the app's requester: the requester resolves an
 * access token for every call, and that resolution ends up here — going through it
 * would be a cycle. This is also the one call in the app that must NOT carry an
 * Authorization header. The endpoint authenticates on the refresh cookie alone,
 * and sending the expired access token alongside it would only invite someone to
 * make it the credential later.
 */
const requestRefresh = async () => {
    let response;

    try {
        response = await fetch(REFRESH_ENDPOINT, {
            method: 'POST',
            // The whole mechanism hangs off this. The refresh cookie is httpOnly, so
            // JavaScript cannot read it and attach it by hand — the browser has to,
            // and cross-origin it only does so in credentials mode. Without this
            // every refresh arrives at the API with no cookie and 401s.
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }
    catch (err) {
        // No answer at all. The session may well be alive — a farm with no signal is
        // the normal case for this app — so the stored token is deliberately left in
        // place for the caller to fall back on.
        return { status: REFRESH_NETWORK_ERROR, token: null };
    }

    if (response.status === 401) {
        // The only answer that means the session is genuinely over. The API reports
        // every reason identically (unknown, expired, revoked) and has already
        // cleared the cookie, so there is nothing to retry and nothing to keep.
        clearAccessToken();
        return { status: REFRESH_SESSION_ENDED, token: null };
    }

    if (!response.ok) {
        return { status: REFRESH_SERVER_ERROR, token: null };
    }

    let body;

    try {
        body = await response.json();
    }
    catch (err) {
        return { status: REFRESH_SERVER_ERROR, token: null };
    }

    if (!body?.authToken) {
        return { status: REFRESH_SERVER_ERROR, token: null };
    }

    await setAccessToken(body.authToken);
    return { status: REFRESH_OK, token: body.authToken };
};

/**
 * Refreshes the session, collapsing concurrent callers onto one request.
 *
 * The app opens with a burst of parallel work — several Apollo queries, the two
 * offline sync loops, the user lookup — and they all find the same expired token at
 * the same moment. Without this, each starts its own refresh. Today that is merely
 * wasteful; once the API rotates refresh tokens (Phase 2), the second request would
 * present a token the first had just consumed, which the API is designed to read as
 * a replay and answer by killing the whole token family. So this is not an
 * optimisation, it is what keeps rotation from logging the user out on every start.
 */
export const refreshSession = () => {
    if (!inFlightRefresh) {
        inFlightRefresh = requestRefresh().finally(() => { inFlightRefresh = null; });
    }

    return inFlightRefresh;
};

/**
 * The token-or-nothing form of `refreshSession`, for callers that only need to know
 * whether they have something to send.
 */
export const refreshAccessToken = async () => (await refreshSession()).token;

/**
 * The access token to put on an outgoing request: the stored one while it is still
 * good, a freshly refreshed one when it is not, or null when there is no session.
 */
export const getValidAccessToken = async () => {
    const token = getAccessToken();

    if (isTokenUsable(token)) {
        return token;
    }

    return refreshAccessToken();
};

/**
 * Revokes the refresh token server-side and clears the cookie. Idempotent, and
 * answers 204 even when there was nothing to revoke.
 *
 * Worth awaiting on an explicit sign-out: dropping the local tokens alone would
 * leave a 30-day credential sitting in the browser, which is exactly what the next
 * visitor to a shared farm-office tablet would resume from.
 */
export const revokeSession = async () => {
    try {
        await fetch(LOGOUT_ENDPOINT, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }
    catch (err) {
        // Offline sign-out. The local tokens still go, and the refresh token lapses
        // on its own; there is nothing useful to report to the user here.
    }
};

/**
 * Ends a session that the server has already rejected: drops what is left locally
 * and sends the user to the login page.
 *
 * A full page navigation rather than a router redirect, because every context in
 * the tree is holding data for a user who no longer has a session. No call to
 * /auth/logout — this is only reached after a refresh came back 401, which means
 * the API has revoked the token and cleared the cookie itself.
 */
export const endSession = () => {
    clearAccessToken();
    if (window.location.pathname.startsWith('/login')) {
        return;
    }

    window.location.replace('/login');
};
